import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHeightAvailable } from "@repoflow/components/content-resize";
import "./style.css";

const MIN_PERCENT = 20;
const MAX_PERCENT = 80;

// Two panes and a gutter you can drag.
//
// The archived project used `react-split` for this. It is not a dependency here
// on purpose: @repoflow/components is a PATH dependency of all four apps, so a
// package added to it has to be installed by four projects that have no other
// use for it. What it does is forty lines of pointer events, and they are below.
//
// Height comes from useHeightAvailable rather than a percentage, for the reason
// every full-height screen in this app does it that way: the panes sit under a
// navbar and a breadcrumb whose height is not known to CSS.
export function Layout({ left, right, initial = 50 }) {
  const { refContent, height } = useHeightAvailable();
  const container = useRef(null);
  const [split, setSplit] = useState(initial);
  const [dragging, setDragging] = useState(false);

  const onPointerMove = useCallback((event) => {
    if (!container.current) {
      return;
    }

    const { left: originX, width } = container.current.getBoundingClientRect();

    if (width === 0) {
      return;
    }

    const percent = ((event.clientX - originX) / width) * 100;
    setSplit(Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, percent)));
  }, []);

  const stop = useCallback(() => setDragging(false), []);

  useEffect(() => {
    if (!dragging) {
      return undefined;
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stop);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stop);
      // The preview iframe sizes itself off useHeightAvailable, which recomputes
      // on window resize and on nothing else. Without this the pane gets wider
      // and the iframe inside it does not.
      window.dispatchEvent(new Event("resize"));
    };
  }, [dragging, onPointerMove, stop]);

  return (
    <>
      <div ref={refContent}></div>
      <div className="layout-dual" ref={container} style={{ height: `${height}px` }}>
        <div className="layout-dual-pane" style={{ width: `${split}%` }}>
          {left}
        </div>
        <div
          className={`layout-dual-gutter${dragging ? " layout-dual-gutter-active" : ""}`}
          onPointerDown={(event) => {
            // Captured so the drag survives the pointer crossing the iframe,
            // which otherwise swallows every event once it is over it.
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
          }}
        />
        <div className="layout-dual-pane" style={{ width: `${100 - split}%` }}>
          {right}
        </div>
      </div>
      {/* While dragging, the iframe must not receive the pointer. */}
      {dragging && <div className="layout-dual-shield" />}
    </>
  );
}
